/** Radio-card pickers for the wiki template (project create/edit, onboarding) and the compile mode (settings, onboarding) */

import { useId, type ReactNode } from 'react'
import { WIKI_TEMPLATE_IDS, WIKI_TEMPLATES, COMPILE_MODES, type CompileMode, type WikiTemplateId } from '@agentwiki/shared'
import { useT } from '../../i18n'
import { useAppStore } from '../../stores/app-store'
import { cn } from '../../lib/utils'
import { hintClass } from '../ui/styles'

interface RadioCardOption<T extends string> {
  value: T
  title: string
  description: ReactNode
}

function RadioCards<T extends string>({ legend, hint, name, value, options, onChange, columns = 2, disabled }: {
  legend: string
  hint?: string
  name: string
  value: T
  options: RadioCardOption<T>[]
  onChange: (value: T) => void
  columns?: 1 | 2
  disabled?: boolean
}) {
  const id = useId()
  return (
    <fieldset className="space-y-2" aria-describedby={hint ? `${id}-hint` : undefined} disabled={disabled}>
      <legend className="block text-sm font-medium text-fg-muted">{legend}</legend>
      {hint && <p id={`${id}-hint`} className={hintClass}>{hint}</p>}
      <div className={cn('grid gap-2', columns === 2 && 'sm:grid-cols-2')}>
        {options.map((option) => {
          const active = option.value === value
          return (
            <label
              key={option.value}
              className={cn(
                'flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border bg-surface-raised p-3 text-left has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent',
                active ? 'border-accent ring-1 ring-accent' : 'border-border hover:border-border-strong',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={active}
                onChange={() => onChange(option.value)}
                className="mt-1 h-4 w-4 shrink-0 accent-accent"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-fg">{option.title}</span>
                <span className="mt-0.5 block text-xs text-fg-muted">{option.description}</span>
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

export function WikiTemplatePicker({ value, onChange, disabled }: { value: WikiTemplateId; onChange: (value: WikiTemplateId) => void; disabled?: boolean }) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const name = useId()
  return (
    <RadioCards
      legend={t('wikiTemplate.label')}
      hint={t('wikiTemplate.hint')}
      name={`wiki-template-${name}`}
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={WIKI_TEMPLATE_IDS.map((id) => {
        const sections = WIKI_TEMPLATES[id].sections
        return {
          value: id,
          title: t(`wikiTemplate.${id}`),
          description: (
            <>
              {t(`wikiTemplate.${id}.hint`)}
              {sections.length > 0 && (
                <span className="mt-1 block text-fg-subtle">
                  {t('wikiTemplate.sections', { sections: sections.map((s) => (locale === 'vi' ? s.titleVi : s.title)).join(', ') })}
                </span>
              )}
            </>
          ),
        }
      })}
    />
  )
}

export function CompileModePicker({ value, onChange, disabled, hint }: { value: CompileMode; onChange: (value: CompileMode) => void; disabled?: boolean; hint?: string }) {
  const t = useT()
  const name = useId()
  return (
    <RadioCards
      legend={t('compileMode.label')}
      hint={hint ?? t('compileMode.hint')}
      name={`compile-mode-${name}`}
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={COMPILE_MODES.map((mode) => ({ value: mode, title: t(`compileMode.${mode}`), description: t(`compileMode.${mode}.hint`) }))}
    />
  )
}
